import { type Brand, type Car } from '@prisma/client'
import { Link } from '@remix-run/react'
import {
  calculateDiscountedPrice,
  formatDiscountedPrice,
  formatPrice,
} from '#app/shared/lib/price.ts'
import CarSpecialFeatures from '#app/shared/ui/data-display/carDescription/CarSpecialFeatures.js'
import { PackOptimaWithModal } from '#app/shared/ui/data-display/PackOptimaComponents.tsx'
import { Separator } from '#app/shared/ui/data-display/Separator.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import {
  EcoLabelEnum,
  TransmissionEnum,
  FuelTypeEnum,
} from '../../../../../types/enums'

type Props = {
  car: Car & {
    brand: Brand
    reservation?: { payment?: { id: string } } | null
  }
  financingAvailable: boolean
}

export default function OrderDetails({ car, financingAvailable }: Props) {
  if (!car) {
    return null
  }

  const etiquetaImages: Record<
    (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum],
    string
  > = {
    [EcoLabelEnum.ECO]: '/img/Etiquetas_Conbustible/Etiqueta_ECO.png',
    [EcoLabelEnum.C]: '/img/Etiquetas_Conbustible/Etiqueta_C.png',
    [EcoLabelEnum.CERO]: '/img/Etiquetas_Conbustible/Etiqueta_0.png',
    [EcoLabelEnum.B]: '/img/Etiquetas_Conbustible/Etiqueta_B.png',
  }

  const features = [
    `${car.matriculation ? car.matriculation.getFullYear() : car.year}`,
    `${car.mileage.toLocaleString('es-ES')} km`,
    `${car.horsePower} cv`,
    Object.values(TransmissionEnum).includes(
      car.transmission as (typeof TransmissionEnum)[keyof typeof TransmissionEnum],
    )
      ? car.transmission
      : 'N/A',
    Object.values(FuelTypeEnum).includes(
      car.fuelType as (typeof FuelTypeEnum)[keyof typeof FuelTypeEnum],
    )
      ? car.fuelType
      : 'N/A',
  ]

  const financedPrice = formatDiscountedPrice(
    car.minFinancedAmount ?? 0,
    car.discount,
  )

  const inCashPrice = formatDiscountedPrice(car.price, car.discount)

  const validEcoLabel = Object.values(EcoLabelEnum).includes(
    car.ecoLabel as (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum],
  )
    ? car.ecoLabel
    : EcoLabelEnum.B

  return (
    <div
      className={`
        w-full

        lg:px-0

        md:px-7
      `}
    >
      <div
        className={`
          relative w-full rounded-base border border-b-1 border-neutral-75
          bg-white pt-11
        `}
      >
        <img
          className="absolute right-5 top-5 h-5 w-5"
          src={
            etiquetaImages[
              validEcoLabel as (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum]
            ]
          }
          alt="eco label"
        />

        <div className={`flex flex-col gap-4 px-5`}>
          <p
            className={`
              text-h5-bold text-neutral-900

              xl:text-h4
            `}
          >
            {car.brand.name} {car.model}
          </p>
          <p
            className="line-clamp-1 text-base-medium text-neutral-700"
            title={car.description}
          >
            {car.description}
          </p>
          <CarSpecialFeatures features={features} />
          <Separator className="w-full bg-neutral-100" />
        </div>

        <div className={`flex items-start justify-between px-5`}>
          <div className="flex min-w-0 flex-1 flex-col gap-2 pt-3">
            <p
              className={`
                whitespace-nowrap text-subtitle2-semibold text-neutral-500
              `}
            >
              AL CONTADO
            </p>
            <p
              className={`
                overflow-hidden whitespace-nowrap text-h4 text-neutral-900

                xl:text-h3
              `}
            >
              {inCashPrice}
            </p>
          </div>

          {financingAvailable && (
            <>
              <Separator
                orientation="vertical"
                className="h-24 bg-neutral-100"
              />

              <div
                className={`
                  relative flex min-w-0 flex-1 flex-col gap-2 px-5 pt-3
                `}
              >
                <p
                  className={`
                    whitespace-nowrap text-subtitle2-semibold text-neutral-500
                  `}
                >
                  FINANCIADO POR
                </p>
                <p
                  className={`
                    overflow-hidden whitespace-nowrap text-h4 text-neutral-900

                    xl:text-h3
                  `}
                >
                  {financedPrice}
                </p>
                <span
                  className={`
                    absolute left-5 top-full pt-0.5 text-xs text-neutral-400
                  `}
                >
                  sólo particulares y autónomos
                </span>
              </div>
            </>
          )}
        </div>
        {financingAvailable && (
          <div
            className={`
              relative mx-auto flex w-full flex-col justify-between
              overflow-hidden border border-neutral-100 px-0

              xl:max-w-[384px] xl:rounded-base
            `}
          >
            <div className="px-5 py-5">
              <p className="mb-5 text-subtitle2-semibold text-neutral-500">
                FINANCIADO DESDE
              </p>
              <div className="flex h-5 items-end justify-between">
                <div className="flex items-end">
                  <span
                    className={`
                      text-h4 text-primary-500

                      xl:text-h3
                    `}
                  >
                    {formatPrice(Math.round(car.minMonthlyPayment ?? 0))}
                  </span>
                  <span className="ml-2 text-base-bold text-neutral-500">
                    /mes
                  </span>
                </div>

                <PackOptimaWithModal
                  size="medium"
                  discountAmount={
                    (calculateDiscountedPrice(
                      car.minFinancedAmount ?? 0,
                      car.discount ?? 0,
                    ) ?? 0) - car.price
                  }
                />
              </div>
            </div>
            {!car.reservation && (
              <Link
                to={`/coche/${car.slug}/reservar?typeOfPayment=Financiado`}
                className={`
                  flex h-8 w-full items-center justify-between border-t
                  border-neutral-100 bg-neutral-25 px-4
                `}
              >
                <p className="text-subtitle1-medium text-neutral-900">
                  Elige tu cuota desde{' '}
                  {formatPrice(Math.round(car.minMonthlyPayment ?? 0))}/mes
                </p>
                <Icon
                  name="basic/chevron-right"
                  className="text-neutral-900"
                  size="12px"
                />
              </Link>
            )}
          </div>
        )}

        <div className={`mt-[14px] flex flex-col gap-3 px-5`}>
          <div className="flex items-center gap-1">
            <Icon name="basic/check" className="text-primary-500" size="10px" />
            <p className="text-subtitle2-semibold text-neutral-500">
              PAGO DE RESERVA CON TARJETAS O TRANSFERENCIA
            </p>
          </div>

          <div className="flex items-center gap-1">
            <Icon name="basic/check" className="text-primary-500" size="10px" />
            <p className="text-subtitle2-semibold text-neutral-500">
              GARANTÍA PREMIUM DE HASTA 3 AÑOS
            </p>
          </div>
        </div>

        <Spacer size="20px" />
        <div
          className={`
            h-[148px] w-full border-t border-neutral-75 px-5 pt-6

            xl:h-[125px] xl:px-0
          `}
        >
          <p
            className={`
              mb-[13px] text-center text-subtitle1-bold text-neutral-900
            `}
          >
            Recógelo gratis en nuestro concesionario
          </p>
          <div className={`flex w-full items-center justify-center gap-[3px]`}>
            <Separator className="w-full bg-neutral-75" />
            <span className="text-subtitle2-semibold text-neutral-400">o</span>
            <Separator className="w-full bg-neutral-75" />
          </div>
          <div
            className={`
              mx-auto w-full bg-neutral-75

              xl:w-[90%]
            `}
          />
          <div className="mt-2.5 flex items-center justify-center gap-2">
            <div
              className={`
                flex h-9 min-h-9 w-9 min-w-9 items-center justify-center
                rounded-full border border-neutral-100 bg-neutral-25
              `}
            >
              <img
                className="h-[12.61px] w-[26px]"
                src="/img/Group 168.svg"
                alt="car-icon"
              />
            </div>
            <div
              className={`
                flex flex-col gap-1.5

                md:pt-2

                xl:pt-0
              `}
            >
              <p className="text-subtitle1-bold text-neutral-900">
                Entrega en la puerta de tu casa.
              </p>
              <p className="text-subtitle1-medium text-neutral-900">
                Gestión de envío en camión a toda España (península)
              </p>
            </div>
          </div>
        </div>
        <Spacer size="24px" />
      </div>
      {!car.reservation && (
        <Link
          to={
            financingAvailable
              ? `/coche/${car.slug}/me-interesa`
              : `/coche/${car.slug}/reservar?typeOfPayment=Al%20contado`
          }
        >
          <Button
            className={`
              hidden h-16 !w-full rounded-b-[6px] bg-primary-500

              xl:block
            `}
          >
            <span className="text-h5-bold text-white">Me Interesa</span>
          </Button>
        </Link>
      )}
    </div>
  )
}
